import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSalons, createSale, updateSale, getSaleById, getPreviousClosing } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  CalendarIcon, 
  Save, 
  ArrowLeft,
  IndianRupee,
  Users,
  Calculator
} from "lucide-react";
import { cn } from "../lib/utils";

const SalesForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPrevious, setFetchingPrevious] = useState(false);
  const [date, setDate] = useState(new Date());
  
  const [formData, setFormData] = useState({
    salon_id: "",
    opening_balance: 0,
    service_sale: 0,
    product_sale: 0,
    membership_sale: 0,
    misc_sale: 0,
    card_sale: 0,
    male_sale: 0,
    male_walkins: 0,
    female_sale: 0,
    female_walkins: 0,
    cash_expenses: 0,
    bank_deposit: 0,
  });

  // Computed values
  const todayTotal = 
    parseFloat(formData.service_sale || 0) + 
    parseFloat(formData.product_sale || 0) + 
    parseFloat(formData.membership_sale || 0) + 
    parseFloat(formData.misc_sale || 0);
  
  const cashSale = todayTotal - parseFloat(formData.card_sale || 0);
  const closingBalance = 
    parseFloat(formData.opening_balance || 0) + 
    cashSale - 
    parseFloat(formData.cash_expenses || 0) -
    parseFloat(formData.bank_deposit || 0);

  useEffect(() => {
    fetchSalons();
    if (isEdit) {
      fetchSaleData();
    }
  }, [id]);

  const fetchSalons = async () => {
    try {
      const response = await getSalons();
      setSalons(response.data);
    } catch (error) {
      toast.error("Failed to fetch salons");
    }
  };

  const fetchSaleData = async () => {
    try {
      const response = await getSaleById(id);
      const sale = response.data;
      setDate(new Date(sale.date));
      setFormData({
        salon_id: sale.salon_id,
        opening_balance: sale.opening_balance,
        service_sale: sale.service_sale,
        product_sale: sale.product_sale,
        membership_sale: sale.membership_sale,
        misc_sale: sale.misc_sale,
        card_sale: sale.card_sale,
        male_sale: sale.male_sale,
        male_walkins: sale.male_walkins,
        female_sale: sale.female_sale,
        female_walkins: sale.female_walkins,
        cash_expenses: sale.cash_expenses,
        bank_deposit: sale.bank_deposit || 0,
      });
    } catch (error) {
      toast.error("Failed to fetch sale data");
      navigate("/reports");
    }
  };

  const fetchPreviousClosing = useCallback(async (salonId, selectedDate) => {
    if (!salonId) return;
    setFetchingPrevious(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const response = await getPreviousClosing(salonId, dateStr);
      if (response.data.closing_balance !== null) {
        setFormData(prev => ({
          ...prev,
          opening_balance: response.data.closing_balance
        }));
        if (response.data.date) {
          toast.info(`Opening balance loaded from ${format(new Date(response.data.date), "MMM d, yyyy")}`);
        }
      }
    } catch (error) {
      console.error("Failed to fetch previous closing:", error);
    } finally {
      setFetchingPrevious(false);
    }
  }, []);

  useEffect(() => {
    if (!isEdit && formData.salon_id && date) {
      fetchPreviousClosing(formData.salon_id, date);
    }
  }, [formData.salon_id, date, isEdit, fetchPreviousClosing]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.salon_id) {
      toast.error("Please select a salon");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        date: format(date, "yyyy-MM-dd"),
      };

      if (isEdit) {
        await updateSale(id, payload);
        toast.success("Sale updated successfully");
      } else {
        await createSale(payload);
        toast.success("Sale recorded successfully");
      }
      navigate("/reports");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save sale");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="sales-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          className="rounded-full"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">
            {isEdit ? "Edit Sale Entry" : "New Daily Sale Entry"}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? "Update the daily sales record" : "Record today's sales data"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select Salon *</Label>
                    <Select 
                      value={formData.salon_id} 
                      onValueChange={(v) => handleChange("salon_id", v)}
                      data-testid="salon-select"
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose salon" />
                      </SelectTrigger>
                      <SelectContent>
                        {salons.map((salon) => (
                          <SelectItem key={salon.id} value={salon.id}>{salon.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !date && "text-muted-foreground"
                          )}
                          data-testid="date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => d && setDate(d)}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Opening Balance (Previous Day's Closing)</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={formData.opening_balance}
                      onChange={(e) => handleChange("opening_balance", parseFloat(e.target.value) || 0)}
                      className="pl-10"
                      placeholder="0"
                      data-testid="opening-balance"
                    />
                  </div>
                  {fetchingPrevious && <p className="text-xs text-muted-foreground">Loading previous balance...</p>}
                </div>
              </CardContent>
            </Card>

            {/* Sales Data */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-primary" />
                  Sales Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Sale Amount</Label>
                    <Input
                      type="number"
                      value={formData.service_sale}
                      onChange={(e) => handleChange("service_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="service-sale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Product Sale Amount</Label>
                    <Input
                      type="number"
                      value={formData.product_sale}
                      onChange={(e) => handleChange("product_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="product-sale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Membership Cards Sale</Label>
                    <Input
                      type="number"
                      value={formData.membership_sale}
                      onChange={(e) => handleChange("membership_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="membership-sale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Miscellaneous Sale</Label>
                    <Input
                      type="number"
                      value={formData.misc_sale}
                      onChange={(e) => handleChange("misc_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="misc-sale"
                    />
                  </div>
                  <div className="space-y-2 sm:col-span-2">
                    <Label>Card/UPI/Bank Transfer Amount</Label>
                    <Input
                      type="number"
                      value={formData.card_sale}
                      onChange={(e) => handleChange("card_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="card-sale"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gender-wise Data */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Gender-wise Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Male Sale Amount</Label>
                    <Input
                      type="number"
                      value={formData.male_sale}
                      onChange={(e) => handleChange("male_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="male-sale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Male Walk-ins</Label>
                    <Input
                      type="number"
                      value={formData.male_walkins}
                      onChange={(e) => handleChange("male_walkins", parseInt(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="male-walkins"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Female Sale Amount</Label>
                    <Input
                      type="number"
                      value={formData.female_sale}
                      onChange={(e) => handleChange("female_sale", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="female-sale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Female Walk-ins</Label>
                    <Input
                      type="number"
                      value={formData.female_walkins}
                      onChange={(e) => handleChange("female_walkins", parseInt(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="female-walkins"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cash Expenses & Bank Deposit */}
            <Card className="border-border/50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Cash Expenses & Bank Deposit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Cash Expenses for the Day</Label>
                    <Input
                      type="number"
                      value={formData.cash_expenses}
                      onChange={(e) => handleChange("cash_expenses", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="cash-expenses"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bank Deposit</Label>
                    <Input
                      type="number"
                      value={formData.bank_deposit}
                      onChange={(e) => handleChange("bank_deposit", parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      data-testid="bank-deposit"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Card */}
          <div className="space-y-6">
            <Card className="border-border/50 shadow-sm sticky top-24">
              <CardHeader className="bg-primary/5 rounded-t-xl">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-primary" />
                  Computed Summary
                </CardTitle>
                <CardDescription>Auto-calculated values</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Today's Total Sale</span>
                    <span className="font-semibold text-lg text-slate-900" data-testid="today-total">
                      {formatCurrency(todayTotal)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Card/UPI Sale</span>
                    <span className="font-medium text-blue-600" data-testid="computed-card-sale">
                      {formatCurrency(formData.card_sale || 0)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Cash Sale</span>
                    <span className="font-medium text-green-600" data-testid="cash-sale-computed">
                      {formatCurrency(cashSale)}
                    </span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Opening Balance</span>
                    <span className="font-medium">{formatCurrency(formData.opening_balance || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Cash Expenses</span>
                    <span className="font-medium text-red-600">- {formatCurrency(formData.cash_expenses || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Bank Deposit</span>
                    <span className="font-medium text-orange-600">- {formatCurrency(formData.bank_deposit || 0)}</span>
                  </div>

                  <div className="flex justify-between items-center py-3 bg-primary/5 -mx-6 px-6 rounded-lg mt-4">
                    <span className="font-semibold text-slate-900">Closing Cash Balance</span>
                    <span className={cn(
                      "font-bold text-xl",
                      closingBalance >= 0 ? "text-primary" : "text-red-600"
                    )} data-testid="closing-balance">
                      {formatCurrency(closingBalance)}
                    </span>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full mt-6" 
                  disabled={loading}
                  data-testid="submit-sale"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : isEdit ? "Update Entry" : "Save Entry"}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SalesForm;
